import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import { trackEvent } from '../analytics'
import {
  numberWithCommas,
  advertiserAnalyticOptions,
  getStep4PhoneNumberFromLocale
} from '../utils/functions'
import { fetchPlans, fetchUpsellOptions, fetchOrderSummary } from '../services/payment'
import { openRazorpay } from '../utils/razorpay'
import { Media } from './Media'
import { PrimaryButton } from '../components/PrimaryButton'
import ImageWithBasePath from '../components/ImageWithBasePath'
import moment from 'moment'
import Drawer from 'antd/lib/drawer'
import { useRouterHook } from '../hooks/useRouterHook'
import classNames from 'classnames'
import { useBeforeunload } from 'react-beforeunload'
import { useRouter } from 'next/router'
import { PAYMENT_HEADLINES } from '@/constants/advertiser'
import { PaymentPlanCard } from '../components/hire/PaymentPlan/PaymentPlanCard'
import { SharedContext } from '@/context/SharedContext'
import { AdvertiserContext } from '@/context/AdvertiserContext'
import { AdvertiserHeader } from './AdvertiserHeader'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { UpsellOptionsList } from '../components/hire/PaymentFlow/Upsell/UpsellOptionsList'
import { OrderSummary } from '../components/hire/PaymentFlow/OrderSummary'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import { AdvertiserButton } from './hire/AdvertiserButton'
import { createRazorpayOrder } from '@/services/razorpayApi'
import { PaymentPlansSkeleton } from '../components/hire/PaymentFlow/skeletons/PaymentPlansSkeleton'
import EastIcon from '@mui/icons-material/East'
import { Modal } from 'antd'
import { isLegacyNewsWebView } from '@/android/bridge'

const useMock = false

async function mockFetchUpsellingOptions() {
  await new Promise((res) => setTimeout(res, 500))
  return {
    options: [
      {
        id: 1,
        units: 1,
        amount: '₹1799',
        crossed_amount: '₹2598',
        discount: '30%',
        text: '1 Job Post with Gold Plan',
        sub_text: '₹900 per job post',
        default: true
      },
      {
        id: 2,
        units: 2,
        amount: '₹2598',
        crossed_amount: '₹3598',
        discount: '28%',
        text: '2 Job Posts with Gold Plan',
        sub_text: '₹1299 per job post',
        default: false,
        highlights: [
          { id: 101, description: 'Unlimited Applies', is_available: true },
          { id: 102, description: '30 days job validity', is_available: true }
        ]
      }
    ]
  }
}

async function mockFetchOrderSummary(packageId, upsellingId) {
  await new Promise((res) => setTimeout(res, 500))
  const unitCount = upsellingId || 1
  const basePrice = 1299
  const total = basePrice * unitCount
  const discountAmount = Math.round(total * 0.3)
  const subTotal = total - discountAmount
  const gstAmount = Math.round(subTotal * 0.18)
  const finalTotal = subTotal + gstAmount

  return {
    title: `${unitCount} item${unitCount > 1 ? 's' : ''}`,
    package: [
      {
        id: packageId,
        title: 'Gold Plan',
        highlights: [
          { id: 201, description: 'Unlimited Applies', is_available: true },
          { id: 202, description: '30 days validity', is_available: true }
        ],
        units: unitCount,
        price: `₹${basePrice} x ${unitCount} = ₹${total}`
      }
    ],
    discount: { percent: '30%', amount: `- ₹${discountAmount}` },
    sub_total: `₹${subTotal}`,
    gst: { percent: '18%', amount: `₹${gstAmount}` },
    total_amount: `₹${finalTotal}`
  }
}

const PaymentComponent = forwardRef(({ formData, submissionId, locale }, ref) => {
  const { loginDetails, utmParams, windowSize } = useContext(SharedContext)

  const {
    totalAmount,
    setTotalAmount,
    setFormData,
    setTempFormData,
    isSuccess,
    setIsSuccess,
    orderId,
    setOrderId,
    enterpriseLoginDetails,
    isPayMentFailed,
    setPayMentFailed,
    setpostJobPresentStep,
    postJobPresentStep,
    languageFromAndroid,
    queryParams
  } = useContext(AdvertiserContext)

  const router = useRouter()
  const { replaceThePageInAdvertiser, navigateToPagesInAdvertiser } = useRouterHook()
  const [timeoutId, setTimeoutId] = useState(null)

  // --- plan selection
  const [plans, setPlans] = useState([])
  const [selectedPlan, setSelectedPlan] = useState(isPayMentFailed?.selectedPlan)
  const [isPlanSelected, setPlanFlag] = useState(!!isPayMentFailed?.selectedPlan)
  const [isPaying, setIsPaying] = useState(false)

  // --- upsell & summary state
  const [upsellOptions, setUpsellOptions] = useState([])
  const [selectedUpsell, setSelectedUpsell] = useState(isPayMentFailed?.selectedUpsell)
  const [orderSummary, setOrderSummary] = useState(null)
  const [loadingUpsells, setLoadingUpsells] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)

  // --- drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [packagesLoader, setPackagesLoader] = useState(true)

  useEffect(() => {
    if (isSuccess === 'default') {
      setModalOpen(false)
    } else {
      setModalOpen(true)
    }
  }, [isSuccess])

  useEffect(() => {
    if (isSuccess !== 'default') {
      if (windowSize <= 640) {
        setIsDrawerOpen(true)
      }
    }
  }, [isSuccess])

  useEffect(() => {
    trackEvent({
      name: 'viewed_choose_plan_screen',
      searchParams: utmParams,
      options: advertiserAnalyticOptions({
        label: 'job_creation_job_details',
        Page_no: 'page_4',
        Event_type: 'impression',
        enterprise: !!enterpriseLoginDetails?.advertiserId
      })
    })
  }, [])

  useEffect(() => {
    if (!formData?.['posting_locations']?.[0]) return

    setPackagesLoader(true)
    fetchPlans({
      locationId: formData?.['posting_locations']?.[0],
      categoryId: formData?.['job_category'],
      isAndroid: typeof Android !== 'undefined'
    })
      .then((results) => {
        setPlans(results)
      })
      .catch((err) => console.error(err))
      .finally(() => setPackagesLoader(false))
  }, [formData?.['posting_locations']?.[0]])

  useEffect(() => {
    if (plans?.length && !selectedPlan) {
      const mostPopularPlan = plans?.find((plan) =>
        plan?.tags?.some((tag) => tag.replace(/\s/g, '').toLowerCase() === 'mostpopular')
      )
      if (mostPopularPlan) {
        setSelectedPlan(mostPopularPlan)
        setPlanFlag(true)
        trackEvent({
          name: `toggled_plan_${mostPopularPlan?.title}`,
          searchParams: utmParams,
          options: advertiserAnalyticOptions({
            label: 'job_creation_job_details',
            Page_no: 'page_4',
            Event_type: 'tap',
            enterprise: !!enterpriseLoginDetails?.advertiserId
          })
        })
      }
    }
  }, [plans])

  // Fetch upsell options whenever plan changes
  useEffect(() => {
    if (!selectedPlan?.id) return
    setSelectedUpsell(null)
    setOrderSummary(null)
    setLoadingUpsells(true)
    const loader = useMock
      ? () => mockFetchUpsellingOptions(selectedPlan.id)
      : () =>
          fetchUpsellOptions({
            packageId: selectedPlan.id,
            isAndroid: typeof Android !== 'undefined'
          })

    loader()
      .then((res) => {
        const opts = res.options || []
        setUpsellOptions(opts)
        const def = opts.find((o) => o.default) ?? opts[0] ?? null
        setSelectedUpsell(def)
      })
      .catch(console.error)
      .finally(() => setLoadingUpsells(false))
  }, [selectedPlan?.id])

  useEffect(() => {
    if (!selectedPlan?.id || !selectedUpsell) return

    setOrderSummary(null)
    setLoadingSummary(true)
    const loader = useMock
      ? () => mockFetchOrderSummary(selectedPlan?.id, selectedUpsell?.id)
      : () =>
          fetchOrderSummary({
            packageId: selectedPlan.id,
            upsellingId: selectedUpsell?.id,
            isAndroid: typeof Android !== 'undefined'
          })

    loader()
      .then((res) => {
        setOrderSummary(res)
        const raw = Number(res.total_amount.replace(/[₹, ]/g, '')) || 0
        setTotalAmount(raw)
      })
      .catch(console.error)
      .finally(() => setLoadingSummary(false))
  }, [selectedUpsell?.id])

  useImperativeHandle(ref, () => ({
    async handlePayments() {
      if (!selectedPlan) {
        setPlanFlag(false)
        return
      }

      if (isLegacyNewsWebView()) { //incase of new app webview for older versions
        Android.submitForm(
          JSON.stringify({
            ...formData,
            job_city_title: formData?.['job_city'],
            job_title: formData['title'],
            job_company_name: formData['company_name'],
            id: submissionId ? submissionId : queryParams?.submission_id
          })
        )

        Android.initiatePaymentFlow(
          JSON.stringify({
            amount: totalAmount * 100,
            display_amount: totalAmount,
            package_id: selectedPlan?.id,
            submission_id: submissionId ? submissionId : queryParams?.submission_id
          })
        )
      }

      setIsPaying(true)
      try {
        const order = await createRazorpayOrder({
          packageId: selectedPlan?.id,
          submissionId: submissionId || formData?.['submissionId'],
          upsellId: selectedUpsell?.id ?? null,
          advertiserId: loginDetails?.id
        })

        setOrderId(order?.id)
        openRazorpay({
          order,
          customer: { name: loginDetails?.name, phone: loginDetails?.mobileNumber },
          onSuccess: () => {
            setIsSuccess(true)
            localStorage.setItem('formData', JSON.stringify({}))
            localStorage.setItem('tempFormData', {
              data: {},
              isPresent: false,
              title: 'Edit your job post'
            })
            setFormData({})
            setTempFormData({})
            trackEvent({
              name: 'paid_job_success',
              searchParams: utmParams,
              options: advertiserAnalyticOptions({
                Event_type: 'tap'
              })
            })
          },
          onFailure: (err) => {
            console.error('Razorpay failed', err)
            setPayMentFailed({ isFailed: true, selectedPlan, selectedUpsell })
            setIsSuccess(false)
            trackEvent({
              name: 'payment_failed',
              searchParams: utmParams,
              options: advertiserAnalyticOptions({
                Event_type: 'tap',
                enterprise: !!enterpriseLoginDetails?.advertiserId
              }),
              isFbPixelThere: false
            })
          }
        })
      } finally {
        setIsPaying(false)
      }
    }
  }))

  useEffect(() => {
    if (isSuccess == true) {
      handleUserActivity()
    }
  }, [isSuccess])

  useEffect(() => {
    if (isDrawerOpen || modalOpen) {
      if (isSuccess == true) handleUserActivity()
      trackEvent({
        name: 'viewed_payment_status_screen',
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          Page_no: 'page_4',
          Event_type: 'tap',
          enterprise: !!enterpriseLoginDetails?.advertiserId
        }),
        isFbPixelThere: false
      })
    }
  }, [isDrawerOpen, modalOpen])

  const handleUserActivity = () => {
    // Clear the existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Set a new timeout for 10 seconds
    const newTimeoutId = setTimeout(() => {
      replaceThePageInAdvertiser({
        pathName: 'advertiserDashboardPage',
        as: 'dashboard/?utm_source=paid_job_posted_successfully'
      })
    }, 3000)

    // Save the new timeout ID
    setTimeoutId(newTimeoutId)
  }

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart']
    const handleActivity = () => {
      if (isSuccess == true) handleUserActivity()
    }
    events.forEach((event) => {
      document.addEventListener(event, handleActivity)
    })

    // Clean up event listeners
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [router, timeoutId])

  useBeforeunload(() => {
    if (isSuccess == true) {
      router.push('/hire/dashboard/?utm_source=paid_job_posted_successfully')
    }
  })

  const backHandler = ({}) => {
    if (isLegacyNewsWebView()) {
      Android.onFinishFlow()
      return;
    }
    if (postJobPresentStep === 4) {
      trackEvent({
        name: `tap_back_plan_page`,
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          label: 'job_creation_job_details',
          Page_no: 'page_4',
          Event_type: 'tap',
          enterprise: !!enterpriseLoginDetails?.advertiserId
        })
      })
      if (!(submissionId || formData?.['submissionId'])) {
        navigateToPagesInAdvertiser({
          pathName: 'advertiserDashboardPage',
          as: 'dashboard'
        })
      }
      setpostJobPresentStep((prv) => (prv != 1 ? prv - 1 : prv))
    }
  }

  return (
    <>
      <div
        className={classNames(
          'relative',
          windowSize > 768 ? 'flex flex-col bg-[#f9f9f9]' : 'px-16'
        )}
        id="advertiser_step4_mobile"
      >
        {/* Header Image for mobiles */}
        {windowSize <= 768 && (
          <div
            className={classNames(
              'absolute top-0 right-0 z-50',
              windowSize > 768 && windowSize <= 900 && 'h-[60px] w-[150px]',
              windowSize > 900 ? 'h-[170px] w-[250px]' : 'h-[138px] w-[130px]'
            )}
          >
            <ImageWithBasePath
              src={windowSize > 900 ? 'payment_group_desktop' : 'paymentGroup'}
              fill
              alt="payment"
            />
          </div>
        )}

        {/* Back Navigation for mobile screen */}
        {windowSize < 768 && (
          <div className="mx-[-4px] flex justify-start py-16">
            <div className="cursor-pointer" onClick={backHandler}>
              <ImageWithBasePath
                src={'post_job_back'}
                width={24}
                height={24}
                alt="back button arrow"
              />
            </div>
          </div>
        )}

        {/* Navigation Bar for desktop screen */}
        {windowSize > 768 && (
          <AdvertiserHeader isPostButtonPresent={false} isDashboardButton={true} />
        )}

        {/* Container division for desktop and mobile */}
        <div
          className={classNames(
            windowSize > 1200 && 'm-[auto] w-[80%]',
            'relative bg-white',
            windowSize >= 768 && 'pt-[48px]',
            windowSize > 768 && windowSize <= 980 && 'px-[32px]',
            windowSize > 980 && windowSize <= 1080 && 'px-[40px]',
            windowSize > 1080 && 'px-[80px]',
            'mt-[20px]',
            windowSize <= 768 ? 'pb-[150px]' : 'pb-[75px]'
          )}
        >
          {/* Header Image for desktops */}
          {windowSize > 768 && (
            <div
              className={classNames(
                'absolute top-0 right-0',
                windowSize > 768 && windowSize <= 900 && 'h-[160px] w-[150px]',
                windowSize > 900 ? 'h-[170px] w-[250px]' : 'h-[138px] w-[130px]'
              )}
            >
              <ImageWithBasePath
                src={windowSize > 900 ? 'payment_group_desktop' : 'paymentGroup'}
                fill
                alt="payment"
              />
            </div>
          )}

          {/* Payment Page Header Info */}
          <div className="flex flex-col gap-4">
            <div
              className={classNames(
                'font-bold text-[#191919]',
                windowSize > 768 ? 'text-[28px] leading-[40px]' : 'text-[22px] leading-[30px]'
              )}
            >
              Select a plan
            </div>

            {/* Payment Page Headlines for mobiles */}
            {windowSize <= 980 && (
              <div className="flex flex-col gap-2">
                {PAYMENT_HEADLINES?.map((item, index) => {
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <ImageWithBasePath src={item?.icon} height={20} width={20} alt={'success'} />

                      <div className="relative z-[50] text-base font-normal leading-20">
                        {item?.text}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Payment Page Headlines for desktops */}
            {windowSize > 980 && (
              <div className="flex items-center gap-4">
                {PAYMENT_HEADLINES?.map((item, index) => {
                  return (
                    <>
                      <div key={index} className="flex items-center gap-2">
                        <ImageWithBasePath
                          src={item?.icon}
                          height={20}
                          width={20}
                          alt={'success'}
                        />

                        <div className="text-base font-normal leading-20">{item?.text}</div>
                      </div>
                      {index !== PAYMENT_HEADLINES?.length - 1 && (
                        <div className="h-5 w-[1px] bg-[#e5e5e5]"></div>
                      )}
                    </>
                  )
                })}
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div
            className={classNames(
              'flex items-center gap-1 rounded-sm border-xs border-solid border-[#CFE8FC] bg-[#E7F3FE] px-sm py-xs',
              windowSize > 768 ? 'my-[48px]' : 'my-[32px]',
              windowSize > 980 && 'max-w-[730px]'
            )}
          >
            <ErrorOutlineIcon className="h-4 w-4 text-[#0E56A8]" />
            <div className="text-base leading-20 text-[#0E56A8]">
              Your post has been saved! Add a plan to make it live and connect with potential
              employees.
            </div>
          </div>

          {/* Payment Plans & Billing Card & Contact Us Info */}
          <div className="mt-[24px] flex flex-col gap-8">
            {/* Payment Plans */}
            <div>
              {/* Payment Plans */}
              <Media greaterThanOrEqual="sm">
                {packagesLoader ? (
                  <PaymentPlansSkeleton windowSize={windowSize} />
                ) : (
                  <>
                    {plans?.length > 0 && (
                      <div
                        className={classNames(
                          'grid gap-4',
                          windowSize > 980 ? 'grid-cols-3' : 'grid-cols-1'
                        )}
                      >
                        {plans.map((plan) => (
                          <PaymentPlanCard
                            key={plan?.id}
                            id={plan?.id}
                            title={plan?.title}
                            amount={plan?.amount}
                            crossed_amount={plan?.crossed_amount}
                            tags={plan?.tags}
                            highlights={plan?.highlights}
                            validity_duration={plan?.validity_duration}
                            verified_leads={plan?.verified_leads}
                            database_leads={plan?.database_leads}
                            isSelected={selectedPlan?.id === plan?.id}
                            showRadio={true}
                            onSelect={() => {
                              setPlanFlag(true)
                              setSelectedPlan(plan)
                              setPayMentFailed((prev) => ({
                                ...prev,
                                isFailed: false,
                                selectedPlan: plan
                              }))
                              trackEvent({
                                name: `toggled_plan_${plan?.title}`,
                                searchParams: utmParams,
                                options: advertiserAnalyticOptions({
                                  label: 'job_creation_job_details',
                                  Page_no: 'page_4',
                                  Event_type: 'tap',
                                  enterprise: !!enterpriseLoginDetails?.advertiserId
                                })
                              })
                            }}
                            windowSize={windowSize}
                            tagColor="text-[white] bg-[white]"
                          />
                        ))}
                      </div>
                    )}
                    <div className="mt-[16px] text-base leading-21 text-[#323232]">
                      *All prices are excluding GST.
                    </div>
                  </>
                )}
              </Media>
            </div>

            {/* 2) Upsell */}
            {selectedPlan && (
              <div className="flex flex-col gap-4 sm:gap-6">
                <div className="text-[22px] font-bold leading-[30px] text-title">
                  Buy more, Save more
                </div>
                <UpsellOptionsList
                  options={upsellOptions}
                  selectedUpsell={selectedUpsell}
                  onSelectUpsell={setSelectedUpsell}
                  windowSize={windowSize}
                  isLoading={loadingUpsells}
                />
              </div>
            )}

            {/* 3) Order summary */}
            {selectedPlan && (
              <div className="flex flex-col gap-4 sm:gap-6">
                <div className="text-[22px] font-bold leading-[30px] text-title">Order Summary</div>
                <OrderSummary summary={orderSummary} isLoading={loadingSummary} />
                <div className="flex-1 text-left text-base leading-20 text-[#666] sm:text-center">
                  We follow fair use and privacy rules. Breaking policies or fraud can lead to
                  account suspension and loss of service fees.
                </div>
              </div>
            )}

            {/* Contact Us Options */}
            {windowSize <= 900 ? (
              <div className="flex items-center justify-center rounded-[100px] bg-[#f9f9f9] py-sm">
                <div className="text-sm font-normal leading-16 text-[#191919]">
                  Contact support at{' '}
                  <span
                    className="font-bold"
                    onClick={() => {
                      trackEvent({
                        name: `tap_contact_support`,
                        searchParams: utmParams,
                        options: advertiserAnalyticOptions({
                          label: 'job_creation_job_details',
                          Page_no: 'page_4',
                          Event_type: 'tap',
                          enterprise: !!enterpriseLoginDetails?.advertiserId
                        })
                      })
                    }}
                  >
                    +91{' '}
                    {getStep4PhoneNumberFromLocale(
                      isLegacyNewsWebView() ? languageFromAndroid : locale
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* Payment Button below to proceed to payment */}
        {windowSize > 768 && (
          <div
            className={classNames(
              'sticky bottom-0 z-[80] w-full border-t-[1px]  border-b-none border-l-none border-r-none border-solid border-t-[#e5e5e5] bg-[#f9f9f9]',
              windowSize > 1200 && 'm-[auto] w-[80%]',
              windowSize > 900 ? '' : 'fixed bottom-0 w-full shadow-paymentCtaShadow'
            )}
          >
            <div
              className={classNames(
                'flex w-full flex-col gap-6 bg-white',
                windowSize > 1080 && 'py-24 px-[80px]',
                windowSize > 980 && windowSize <= 1080 && 'py-24 px-[40px]',
                windowSize > 768 && windowSize <= 980 && 'py-24 px-[32px]',
                windowSize > 480 && windowSize <= 768 && 'py-16 px-[24px]'
              )}
            >
              <div className="flex items-center gap-2">
                <CallOutlinedIcon className="h-5 w-5 text-[#323232]" />
                <div className="text-base leading-21 text-[#323232]">
                  <span>For any queries, reach us at</span>{' '}
                  <span className="font-bold">
                    +91{' '}
                    {getStep4PhoneNumberFromLocale(
                      isLegacyNewsWebView() ? languageFromAndroid : locale
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-6">
                <AdvertiserButton
                  label={submissionId || formData?.['submissionId'] ? 'Edit Job Post' : 'Back'}
                  className="h-11 w-[146px] gap-2 rounded-[4px] border-xs border-solid border-[#989898] bg-white text-md font-bold leading-24 text-title outline-none"
                  onClick={() => {
                    if (submissionId || formData?.['submissionId']) {
                      setpostJobPresentStep(1)
                    } else {
                      navigateToPagesInAdvertiser({
                        pathName: 'advertiserDashboardPage',
                        as: 'dashboard'
                      })
                    }
                  }}
                />
                <AdvertiserButton
                  endIcon={isPlanSelected && totalAmount ? <EastIcon className="h-4 w-4 text-title" /> : null}
                  disabled={!isPlanSelected || isPaying || !totalAmount}
                  loading={isPaying}
                  label={`${
                    !isPlanSelected || !totalAmount
                      ? 'Continue'
                      : `Proceed to Pay ₹${numberWithCommas(parseInt(totalAmount))}`
                  }`}
                  className={`h-11 rounded-[3px] border-none py-[12px] px-[20px] text-md font-bold leading-24 ${
                    isPlanSelected && totalAmount
                      ? 'flex min-w-[152px] items-center gap-1 bg-[#FFBB00] text-title'
                      : 'w-[152px] bg-[#CBCBCB] text-[#FFFFFF]'
                  }`}
                  onClick={() => {
                    ref.current.handlePayments()
                    trackEvent({
                      name: 'tap_continue_to_pay_amount',
                      searchParams: utmParams,
                      options: advertiserAnalyticOptions({
                        label: 'job_creation_job_details',
                        Page_no: 'page_4',
                        Event_type: 'tap',
                        enterprise: !!enterpriseLoginDetails?.advertiserId
                      })
                    })
                  }}
                />
              </div>
            </div>
            {windowSize > 1200 && <div className="h-5 w-full bg-[#f9f9f9]" />}
          </div>
        )}

        <Media greaterThanOrEqual="md">
          <Modal
            open={modalOpen}
            onCancel={() => {
              setModalOpen(false)
              setIsSuccess('default')
            }}
            className="flex justify-center"
          >
            <div className=" z-100 flex  w-[461px] flex-col items-center justify-center bg-[white] align-middle">
              {isSuccess === true ? (
                <div className="flex justify-center pt-[32px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={72}
                    height={72}
                    viewBox={'0 0 72 72'}
                    fill={'none'}
                  >
                    <mask
                      id="mask0_1_3138"
                      maskUnits="userSpaceOnUse"
                      x={0}
                      y={0}
                      width={72}
                      height={72}
                    >
                      <rect width={72} height={72} fill={'#D9D9D9'} />
                    </mask>
                    <g mask={'url(#mask0_1_3138)'}>
                      <path
                        d="M31.8 41.4L25.35 34.95C24.8 34.4 24.1 34.125 23.25 34.125C22.4 34.125 21.7 34.4 21.15 34.95C20.6 35.5 20.325 36.2 20.325 37.05C20.325 37.9 20.6 38.6 21.15 39.15L29.7 47.7C30.3 48.3 31 48.6 31.8 48.6C32.6 48.6 33.3 48.3 33.9 47.7L50.85 30.75C51.4 30.2 51.675 29.5 51.675 28.65C51.675 27.8 51.4 27.1 50.85 26.55C50.3 26 49.6 25.725 48.75 25.725C47.9 25.725 47.2 26 46.65 26.55L31.8 41.4ZM36 66C31.85 66 27.95 65.2125 24.3 63.6375C20.65 62.0625 17.475 59.925 14.775 57.225C12.075 54.525 9.9375 51.35 8.3625 47.7C6.7875 44.05 6 40.15 6 36C6 31.85 6.7875 27.95 8.3625 24.3C9.9375 20.65 12.075 17.475 14.775 14.775C17.475 12.075 20.65 9.9375 24.3 8.3625C27.95 6.7875 31.85 6 36 6C40.15 6 44.05 6.7875 47.7 8.3625C51.35 9.9375 54.525 12.075 57.225 14.775C59.925 17.475 62.0625 20.65 63.6375 24.3C65.2125 27.95 66 31.85 66 36C66 40.15 65.2125 44.05 63.6375 47.7C62.0625 51.35 59.925 54.525 57.225 57.225C54.525 59.925 51.35 62.0625 47.7 63.6375C44.05 65.2125 40.15 66 36 66ZM36 60C42.7 60 48.375 57.675 53.025 53.025C57.675 48.375 60 42.7 60 36C60 29.3 57.675 23.625 53.025 18.975C48.375 14.325 42.7 12 36 12C29.3 12 23.625 14.325 18.975 18.975C14.325 23.625 12 29.3 12 36C12 42.7 14.325 48.375 18.975 53.025C23.625 57.675 29.3 60 36 60Z"
                        fill={'#028A12'}
                      />
                    </g>
                  </svg>
                </div>
              ) : (
                <div className="flex justify-center pt-[32px]">
                  <ImageWithBasePath src="ic_error" width={72} height={72} alt="jobStatusIcon" />
                </div>
              )}
              {
                <div className="mt-[24px] flex justify-center font-noto-sans text-[20px] font-bold leading-[28px]">
                  ₹{numberWithCommas(parseInt(totalAmount))}
                </div>
              }
              {isSuccess === true ? (
                <div className="text-center font-noto-sans text-[20px] font-bold leading-[28px]">
                  Payment Successful!
                </div>
              ) : (
                <div className="text-center font-noto-sans text-[20px] font-bold leading-[28px]">
                  Payment failed!
                </div>
              )}
              <div className="mt-[24px] text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#666]">
                {isSuccess === true ? 'Paid' : 'Failed'} on {moment().format('DD MMM, YYYY')} for
              </div>
              <div className="mt-[4px] w-[360px] truncate	text-ellipsis  text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#191919]">
                {formData?.title}
              </div>
              <div className="text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#191919]">
                {formData?.company_name}
              </div>
              {isSuccess && (
                <div className="flex">
                  <div className="mt-[24px] text-center font-noto-sans text-[14px] font-normal leading-[21x] text-[#666]">
                    Order ID
                  </div>
                  <div className="ml-[4px] mt-[24px] text-center font-noto-sans text-[14px] font-normal leading-[21x] text-[#323232]">
                    {orderId}
                  </div>
                </div>
              )}
              <div className=" w-[100%]">
                {isSuccess === true ? (
                  <div className=" p-[16px]">
                    <PrimaryButton
                      onClick={() => {
                        localStorage.setItem('formData', JSON.stringify({}))
                        localStorage.setItem('tempFormData', {
                          data: {},
                          isPresent: false,
                          title: 'Edit your job post'
                        })
                        setFormData({})
                        setTempFormData({})
                        if (typeof window !== 'undefined') {
                          replaceThePageInAdvertiser({
                            pathName: 'advertiserDashboardPage',
                            as: 'dashboard/?utm_source=paid_job_posted_successfully'
                          })
                        }
                        trackEvent({
                          name: 'tap_okay_payment_successful',
                          searchParams: utmParams,
                          options: advertiserAnalyticOptions({
                            Event_type: 'tap',
                            enterprise: !!enterpriseLoginDetails?.advertiserId
                          }),
                          isFbPixelThere: false
                        })
                      }}
                      title={'Go  to jobs dashboard'}
                      className="border-none border-[#FB0] bg-[#FB0] font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                    />
                  </div>
                ) : (
                  <div className="flex justify-between space-x-[10px] p-[16px]">
                    <div>
                      <PrimaryButton
                        onClick={() => {
                          localStorage.setItem('formData', JSON.stringify({}))
                          localStorage.setItem('tempFormData', {
                            data: {},
                            isPresent: false,
                            title: 'Edit your job post'
                          })
                          setFormData({})
                          setTempFormData({})
                          if (typeof window !== 'undefined') {
                            trackEvent({
                              name: 'payment_failed',
                              searchParams: utmParams,
                              options: advertiserAnalyticOptions({
                                Event_type: 'tap',
                                enterprise: !!enterpriseLoginDetails?.advertiserId
                              }),
                              isFbPixelThere: false
                            })
                            replaceThePageInAdvertiser({
                              pathName: 'advertiserDashboardPage',
                              as: 'dashboard/?utm_source=payment_failed'
                            })
                          }
                        }}
                        title={'Go to jobs dashboard'}
                        className="border-1 rounded-8 w-[196px] border-[#4B4B4B] bg-[white]  font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                      />
                    </div>
                    <div className="pl-[16px]">
                      <PrimaryButton
                        onClick={() => {
                          setModalOpen(false)
                          setIsSuccess('default')
                          ref.current.handlePayments()
                          trackEvent({
                            name: 'tap_retry_payment',
                            searchParams: utmParams,
                            options: advertiserAnalyticOptions({
                              Event_type: 'tap',
                              enterprise: !!enterpriseLoginDetails?.advertiserId
                            }),
                            isFbPixelThere: false
                          })
                        }}
                        title={'Retry payment'}
                        className=" w-[196px] border-none border-[#FB0] bg-[#FB0] font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal>
        </Media>

        {/* Success / Failure Payment Drawers after payment */}
        {isDrawerOpen && (
          <Drawer
            title={
              <div
                className="cursor-pointer"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    replaceThePageInAdvertiser({
                      pathName: 'advertiserDashboardPage',
                      as: isSuccess
                        ? 'dashboard/?utm_source=paid_job_posted_successfully'
                        : 'dashboard/?utm_source=payment_failed'
                    })
                  }
                  trackEvent({
                    name: 'tap_back_payment_status_screen',
                    searchParams: utmParams,
                    options: advertiserAnalyticOptions({
                      Event_type: 'tap',
                      enterprise: !!enterpriseLoginDetails?.advertiserId
                    }),
                    isFbPixelThere: false
                  })
                  if (!isSuccess) {
                    trackEvent({
                      name: 'payment_failed',
                      searchParams: utmParams,
                      options: advertiserAnalyticOptions({
                        Event_type: 'tap',
                        enterprise: !!enterpriseLoginDetails?.advertiserId
                      }),
                      isFbPixelThere: false
                    })
                  }
                }}
              >
                <ImageWithBasePath
                  src={'post_job_back'}
                  width={24}
                  height={24}
                  alt="back button arrow"
                />
              </div>
            }
            open={isDrawerOpen}
            width="100%"
            closable={false}
            className="payment-status z-100"
          >
            <div>
              <div className=" flex h-[80vh] w-[100%] flex-col items-center justify-center p-[16px] align-middle">
                <div className="flex justify-center">
                  {isSuccess === true ? (
                    <div className="flex justify-center pt-[32px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={72}
                        height={72}
                        viewBox={'0 0 72 72'}
                        fill={'none'}
                      >
                        <mask
                          id="mask0_1_3138"
                          maskUnits="userSpaceOnUse"
                          x={0}
                          y={0}
                          width={72}
                          height={72}
                        >
                          <rect width={72} height={72} fill={'#D9D9D9'} />
                        </mask>
                        <g mask={'url(#mask0_1_3138)'}>
                          <path
                            d="M31.8 41.4L25.35 34.95C24.8 34.4 24.1 34.125 23.25 34.125C22.4 34.125 21.7 34.4 21.15 34.95C20.6 35.5 20.325 36.2 20.325 37.05C20.325 37.9 20.6 38.6 21.15 39.15L29.7 47.7C30.3 48.3 31 48.6 31.8 48.6C32.6 48.6 33.3 48.3 33.9 47.7L50.85 30.75C51.4 30.2 51.675 29.5 51.675 28.65C51.675 27.8 51.4 27.1 50.85 26.55C50.3 26 49.6 25.725 48.75 25.725C47.9 25.725 47.2 26 46.65 26.55L31.8 41.4ZM36 66C31.85 66 27.95 65.2125 24.3 63.6375C20.65 62.0625 17.475 59.925 14.775 57.225C12.075 54.525 9.9375 51.35 8.3625 47.7C6.7875 44.05 6 40.15 6 36C6 31.85 6.7875 27.95 8.3625 24.3C9.9375 20.65 12.075 17.475 14.775 14.775C17.475 12.075 20.65 9.9375 24.3 8.3625C27.95 6.7875 31.85 6 36 6C40.15 6 44.05 6.7875 47.7 8.3625C51.35 9.9375 54.525 12.075 57.225 14.775C59.925 17.475 62.0625 20.65 63.6375 24.3C65.2125 27.95 66 31.85 66 36C66 40.15 65.2125 44.05 63.6375 47.7C62.0625 51.35 59.925 54.525 57.225 57.225C54.525 59.925 51.35 62.0625 47.7 63.6375C44.05 65.2125 40.15 66 36 66ZM36 60C42.7 60 48.375 57.675 53.025 53.025C57.675 48.375 60 42.7 60 36C60 29.3 57.675 23.625 53.025 18.975C48.375 14.325 42.7 12 36 12C29.3 12 23.625 14.325 18.975 18.975C14.325 23.625 12 29.3 12 36C12 42.7 14.325 48.375 18.975 53.025C23.625 57.675 29.3 60 36 60Z"
                            fill={'#028A12'}
                          />
                        </g>
                      </svg>
                    </div>
                  ) : (
                    <div className="flex justify-center pt-[32px]">
                      <ImageWithBasePath
                        src="ic_error"
                        width={72}
                        height={72}
                        alt="jobStatusIcon"
                      />
                    </div>
                  )}
                </div>
                {
                  <div className="mt-[24px] flex justify-center font-noto-sans text-[20px] font-bold leading-[28px]">
                    ₹{numberWithCommas(parseInt(totalAmount))}
                  </div>
                }
                {isSuccess ? (
                  <div className="text-center font-noto-sans text-[20px] font-bold leading-[28px]">
                    Payment Successful!
                  </div>
                ) : (
                  <div className="text-center font-noto-sans text-[20px] font-bold leading-[28px]">
                    Payment failed!
                  </div>
                )}

                <div className="mt-[24px] text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#666]">
                  {isSuccess ? 'Paid' : 'Failed'} on {moment().format('DD MMM, YYYY')} for
                </div>
                <div className="mt-[4px] w-[360px] truncate	text-ellipsis  text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#191919]">
                  {formData?.title}
                </div>
                <div className="text-center font-noto-sans text-[14px] font-normal leading-[21px] text-[#191919]">
                  {formData?.company_name}
                </div>
                {isSuccess && (
                  <>
                    <div className="mt-[24px] text-center font-noto-sans text-[14px] font-normal leading-[21x] text-[#666]">
                      Order ID
                    </div>
                    <div className="text-center font-noto-sans text-[14px] font-normal leading-[21x] text-[#323232]">
                      {orderId}
                    </div>
                  </>
                )}

                <div className="absolute bottom-0  w-[100%] p-[16px]">
                  {isSuccess === true ? (
                    <div className=" w-full">
                      <PrimaryButton
                        onClick={() => {
                          localStorage.setItem('formData', JSON.stringify({}))
                          localStorage.setItem('tempFormData', {
                            data: {},
                            isPresent: false,
                            title: 'Edit your job post'
                          })
                          setFormData({})
                          setTempFormData({})
                          setIsDrawerOpen(false)
                          if (typeof window !== 'undefined') {
                            replaceThePageInAdvertiser({
                              pathName: 'advertiserDashboardPage',
                              as: 'dashboard/?utm_source=paid_job_posted_successfully'
                            })
                          }
                          trackEvent({
                            name: 'tap_okay_payment_successful',
                            searchParams: utmParams,
                            options: advertiserAnalyticOptions({
                              Event_type: 'tap',
                              enterprise: !!enterpriseLoginDetails?.advertiserId
                            }),
                            isFbPixelThere: false
                          })
                        }}
                        title={'Go  to jobs dashboard'}
                        className=" rounded-[8px] border-none border-[#FB0] bg-[#FB0] font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                      />
                    </div>
                  ) : (
                    <div className=" w-[100%]">
                      <div className="w-full pb-[10px]">
                        <PrimaryButton
                          isArrowPresent
                          onClick={() => {
                            setIsDrawerOpen(false)
                            trackEvent({
                              name: 'tap_retry_payment',
                              searchParams: utmParams,
                              options: advertiserAnalyticOptions({
                                Event_type: 'tap',
                                enterprise: !!enterpriseLoginDetails?.advertiserId
                              }),
                              isFbPixelThere: false
                            })
                            ref.current.handlePayments()
                          }}
                          title={'Retry payment'}
                          className="rounded-[8px] border-none border-[#FB0] bg-[#FB0] font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                        />
                      </div>
                      <div className="w-[100%]">
                        <PrimaryButton
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              localStorage.setItem('formData', JSON.stringify({}))
                              localStorage.setItem('tempFormData', {
                                data: {},
                                isPresent: false,
                                title: 'Edit your job post'
                              })
                              setFormData({})
                              setTempFormData({})
                              setIsDrawerOpen(false)
                              trackEvent({
                                name: 'payment_failed',
                                searchParams: utmParams,
                                options: advertiserAnalyticOptions({
                                  Event_type: 'tap',
                                  enterprise: !!enterpriseLoginDetails?.advertiserId
                                }),
                                isFbPixelThere: false
                              })
                              replaceThePageInAdvertiser({
                                pathName: 'advertiserDashboardPage',
                                as: 'dashboard/?utm_source=payment_failed'
                              })
                            }
                          }}
                          title={'Go to jobs dashboard'}
                          className="border-1 rounded-[8px]  border-[#4B4B4B] bg-[white]  font-noto-sans text-[14px] font-bold leading-[21x] text-[#333436]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Drawer>
        )}
      </div>

      {/* Payment Button below to proceed to payment for mweb */}
      {windowSize <= 768 && (
        <div
          className={classNames(
            'fixed bottom-0 z-[80] flex w-[100%] justify-between border-t-[1px]  border-b-none border-l-none border-r-none border-solid border-t-[#e5e5e5] bg-[#fff] p-16 shadow-paymentCtaShadow'
          )}
        >
          <div className="flex w-full items-center justify-between gap-3">
            {(submissionId || formData?.['submissionId']) && !isLegacyNewsWebView() && (
              <AdvertiserButton
                label="Edit Job"
                className="h-11 w-[120px] gap-2 rounded-[4px] border-xs border-solid border-[#989898] bg-white text-md font-bold leading-24 text-title outline-none"
                onClick={() => {
                  setpostJobPresentStep(1)
                }}
              />
            )}
            <AdvertiserButton
              endIcon={isPlanSelected && totalAmount ? <EastIcon className="h-4 w-4 text-title" /> : null}
              disabled={!isPlanSelected || isPaying || !totalAmount}
              loading={isPaying}
              label={`${
                !isPlanSelected || !totalAmount ? 'Continue' : `Pay ₹${numberWithCommas(parseInt(totalAmount))}`
              }`}
              className={`h-11 flex-1 rounded-[3px] border-none py-[12px] px-[20px] text-md font-bold leading-24 ${
                isPlanSelected && totalAmount
                  ? 'gap-1 bg-[#FFBB00] text-title'
                  : 'w-[152px] bg-[#CBCBCB] text-[#FFFFFF]'
              }`}
              onClick={() => {
                ref.current.handlePayments()
                trackEvent({
                  name: 'tap_continue_to_pay_amount',
                  searchParams: utmParams,
                  options: advertiserAnalyticOptions({
                    label: 'job_creation_job_details',
                    Page_no: 'page_4',
                    Event_type: 'tap',
                    enterprise: !!enterpriseLoginDetails?.advertiserId
                  })
                })
              }}
            />
          </div>
        </div>
      )}
    </>
  )
})

export default PaymentComponent
